import React, { useEffect, useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { useRecentlyViewed } from "../hooks/useRecentlyViewed";
import ProductCard from "./ProductCard";

/**
 * Horizontally scrollable "Recently Viewed" carousel.
 * - Hidden entirely when there's nothing meaningful to show.
 * - Excludes the currently-viewed product (passed via `excludeId`).
 */
export default function RecentlyViewed({ excludeId = null, eyebrow = "Recently viewed", heading = "Pick up where you left off." }) {
  const { ids } = useRecentlyViewed();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const scrollerRef = useRef(null);

  const filteredIds = ids.filter((id) => id !== excludeId);

  useEffect(() => {
    if (filteredIds.length === 0) {
      setProducts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Fetch in parallel; tolerate individual 404s (deleted/migrated products).
        const settled = await Promise.allSettled(
          filteredIds.map((id) => api.get(`/products/${id}`))
        );
        if (cancelled) return;
        const ordered = settled
          .map((r, i) => (r.status === "fulfilled" ? r.value.data : null))
          .filter(Boolean);
        setProducts(ordered);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredIds.join("|")]);

  if (products.length === 0 && !loading) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.85) * dir;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <section className="mt-32" data-testid="recently-viewed-section">
      <div className="flex items-end justify-between mb-12 gap-6">
        <div>
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="font-display text-3xl md:text-5xl mt-3 tracking-tighter">
            {heading}
          </h2>
        </div>
        {products.length > 2 && (
          <div className="hidden md:flex items-center gap-2" data-testid="recently-viewed-controls">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-12 h-12 border border-[var(--mn-ink)] flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
              aria-label="Scroll left"
              data-testid="recently-viewed-prev"
            >
              <ChevronLeft size={18} strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-12 h-12 border border-[var(--mn-ink)] flex items-center justify-center hover:bg-[var(--mn-ink)] hover:text-white transition-colors"
              aria-label="Scroll right"
              data-testid="recently-viewed-next"
            >
              <ChevronRight size={18} strokeWidth={1.5} />
            </button>
          </div>
        )}
      </div>
      <div
        ref={scrollerRef}
        className="flex gap-6 md:gap-10 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth no-scrollbar"
        data-testid="recently-viewed-scroller"
      >
        {products.map((p) => (
          <div
            key={p.id}
            className="snap-start shrink-0 w-[70%] sm:w-[45%] md:w-[28%] lg:w-[22%]"
          >
            <ProductCard product={p} />
          </div>
        ))}
      </div>
    </section>
  );
}
